import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/device";

const KEY_TOKEN = "tierpt_user_token";
const KEY_USER = "tierpt_user";

const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateUser: () => {},
  setSession: () => {},
  loading: false,
});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(KEY_TOKEN));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY_USER);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  // Attach the user token to axios defaults when present.
  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  // Validate token on mount — if backend rejects it, clear state.
  useEffect(() => {
    if (!token) return;
    api
      .get("/users/me")
      .then((r) => {
        // patch 30: keep the same object when nothing changed, so screens keyed on `user` don't load twice
        const fresh = JSON.stringify(r.data);
        setUser((prev) => (prev && JSON.stringify(prev) === fresh ? prev : r.data));
        localStorage.setItem(KEY_USER, fresh);
      })
      .catch(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem(KEY_TOKEN);
        localStorage.removeItem(KEY_USER);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(identifier, password) {
    setLoading(true);
    try {
      const { data } = await api.post("/users/login", { identifier, password });
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem(KEY_TOKEN, data.token);
      localStorage.setItem(KEY_USER, JSON.stringify(data.user));
      return data.user;
    } finally {
      setLoading(false);
    }
  }

  async function register(username, email, password, hp = "", ref = "") {
    setLoading(true);
    try {
      const { data } = await api.post("/users/register", { username, email, password, hp, device_id: getDeviceId(), ref });
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem(KEY_TOKEN, data.token);
      localStorage.setItem(KEY_USER, JSON.stringify(data.user));
      return data.user;
    } finally {
      setLoading(false);
    }
  }

  // sign in with a token/user pair returned by another endpoint (e.g. account recovery)
  function setSession(newToken, newUser) {
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem(KEY_TOKEN, newToken);
    localStorage.setItem(KEY_USER, JSON.stringify(newUser));
  }

  function logout() {
    setToken(null);
    setUser(null);
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_USER);
  }

  function updateUser(patch) {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem(KEY_USER, JSON.stringify(next));
      return next;
    });
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, updateUser, setSession, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
